/**
 * Daten-Model zu einem Eingabefeld
 *
 * Hält Infos wie den Wert, die Fehlermeldung und
 * die Seite auf der sich das Element befindet vor
 *
 */
export interface InputFieldState {
    value?: string | number | boolean,
    required?: boolean,
    errorMessage?: string,
    page?: number,
    editable?: boolean,
    isChecked?: boolean,
}

export class InputFieldStateClass implements InputFieldState {
    constructor(public value?: string | number | boolean,
                public required?: boolean,
                public errorMessage?: string | null,
                public page?: number,
                public editable?: boolean,
                public isChecked?: boolean) {
    }
}

export function setInputField(setter: Function, object: any): void {
    if (object instanceof InputFieldStateClass) {
        setter(setInputFieldValue(object, object.value));
    } else {
        setter(Object.assign({}, object));
    }
}

export function setInputFieldValue(inputField: InputFieldState, value: string | number | boolean): InputFieldState {
    return new InputFieldStateClass(value, inputField.required, null, inputField.page,
        inputField.editable, inputField.isChecked);
}

export function copyInputField(inputField: InputFieldState): InputFieldState {
    return new InputFieldStateClass(inputField.value, inputField.required, null, inputField.page,
        inputField.editable, inputField.isChecked);
}

/**
 * set the input field error message.
 * @param inputField
 * @param errorMessage
 */
export function setInputFieldErrorMessage(inputField: InputFieldState, errorMessage: string): InputFieldState {
    return new InputFieldStateClass(inputField.value, inputField.required, errorMessage, inputField.page,
        inputField.editable, inputField.isChecked);
}

/**
 * check input field is required.
 * @param inputField
 * @param errorMessage
 */
export function checkRequired(inputField: InputFieldState, errorMessage: string) {
    if (inputField.required && !inputField.value) {
        return setInputFieldErrorMessage(inputField, errorMessage);
    }
    return inputField;
}

/**
 * check input field deep. go recursive deeper and deeper and check the required fields.
 * @param obj
 * @param errorText
 * @param fieldsToCheck
 */
export function checkRequiredDeep(obj: any, errorText?: string, fieldsToCheck?: string[]): boolean {
    let error: boolean = false;

    for (let key in obj) {

        if (fieldsToCheck && fieldsToCheck.indexOf(key) == -1) {
            continue;
        }

        let result = obj[key];
        if (typeof result === 'object') {
            if (result instanceof InputFieldStateClass) {
                let errorMessage = errorText ?? 'Ist ein Pflichtfeld';
                result = checkRequired(result, errorMessage);
                obj[key] = result;
                if (result.errorMessage) {
                    error = true;
                }
            } else {
                error = checkRequiredDeep(result, errorText) || error;
            }
        }
    }

    return error;
}

/**
 * initial input field state. optional init value.
 * @param value
 * @param required
 */
export function inputFieldInitialState(value?: any, required: boolean = false): InputFieldState {
    return new InputFieldStateClass((value || value === 0 || typeof value === 'boolean') ? value : '', required);
}

/**
 * set error message from reason response. if no parent so all messages under the field are for one inputFieldState.
 * is there an parent, exactly one message is for the inputFieldState.
 * @param setter
 * @param inputFieldState
 * @param errorMessage
 * @param field
 * @param parent
 */
export function setErrorMessage(setter: (inputFieldState: InputFieldStateClass) => void, inputFieldState: InputFieldStateClass, errorMessage: any, field: string, parent?: string) {
    if (errorMessage && inputFieldState) {
        let newState: InputFieldStateClass;
        if (parent && errorMessage[parent] && errorMessage[parent][field]) {
            // parent so one message for the inputFieldState
            newState = setMessageText(errorMessage[parent][field], inputFieldState);
        } else {
            // no parent so all messages for the field are important for the inputFieldState
            newState = setMessageText(errorMessage[field], inputFieldState);
        }
        setter(newState);
    }
}

/**
 * set the error messages text for one field.
 * @param errorMessage
 * @param inputFieldState
 */
function setMessageText(errorMessage: any, inputFieldState: InputFieldStateClass) {
    let message = getMessageText(errorMessage);
    let newState: InputFieldStateClass;
    newState = setInputFieldErrorMessage(inputFieldState, message);
    return newState;
}

/**
 * get message text.
 * @param errorMessage
 */
export function getMessageText(errorMessage: any) {
    let message = '';
    // error message can be more for the same inputFieldState
    if (typeof errorMessage === 'object') {
        for (let key in errorMessage) {
            if (typeof errorMessage[key] === 'object') {
                message += getMessageText(errorMessage[key]);
            } else {
                message += errorMessage[key] ? (errorMessage[key] + '\n') : '';
            }
        }
    } else if (errorMessage) {
        message = errorMessage;
    }
    return message;
}

/**
 * check all properties in object recursive. is there an inputFieldState so set it.
 * @param obj
 * @param errorMessage
 * @param setter
 * @param parent
 */
export function setErrorMessages(obj: any, errorMessage: any, setter?: (obj: any) => void, parent?: string) {
    if (errorMessage && obj && typeof obj === 'object') {
        if (Array.isArray(obj)) {
            for (let i = 0; i < obj.length; i++) {
                if (typeof obj[i] === 'object' && obj[i] instanceof InputFieldStateClass) {
                    // input field state so set it
                    setErrorMessage(obj[i], errorMessage, undefined, i.toString());
                } else if (!parent || (parent && errorMessage[parent] && errorMessage[parent][i])) {
                    // deep no parent okay check it, parent so check if there is an error message
                    setErrorMessages(obj[i], parent ? errorMessage[parent] : errorMessage, undefined, i.toString());
                }
            }
        } else {
            for (let key in obj) {
                if (typeof obj[key] === 'object' && obj[key] instanceof InputFieldStateClass) {
                    setErrorMessage((inputFieldState => {
                        obj[key] = inputFieldState;
                    }), obj[key], errorMessage, key, parent);
                } else {
                    // deep
                    setErrorMessages(obj[key], parent ? errorMessage[parent] : errorMessage, undefined, key);
                }
            }
        }

        setter && setter(Object.assign({}, obj));
    }
}

/**
 * hydrate the data. hydrate recursive. if the result should be an InputFieldStateClass convert it.
 * @param data
 * @param result
 */
export function hydrate(data: any, result: any): any {

    for (let key in data) {
        if (result && key in result) {
            if (!(data[key] instanceof Array) && data[key] && typeof data[key] === 'object') {
                hydrate(data[key], result[key]);
            } else if (typeof result[key] === 'object' && result[key] instanceof InputFieldStateClass) {
                result[key] = inputFieldInitialState(data[key], !!result[key].required);
            } else {
                result[key] = data[key];
            }
        }
    }

    return result;
}

/**
 * dehydrate the data. dehydrate recursive.
 * @param source
 * @param result
 */
export function extract(source: any, result: any) {
    for (let key in source) {
        if (source[key] && typeof source[key] === 'object') {
            if (source[key] instanceof InputFieldStateClass) {
                result[key] = source[key].value;
            } else {
                result[key] = Array.isArray(source[key]) ? [] : {};
                extract(source[key], result[key]);
            }
        } else {
            result[key] = source[key];
        }
    }

    return result;
}
